import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { AUTH_EVENTS } from './AuthContext';
import { useAuthEvents } from '../hooks/useAuthEvents';

const GET_CART_DETAILS = gql`
  query getCartDetails($cartId: String!) {
    cart(cart_id: $cartId) {
      id
      items {
        id
        product {
          id
          name
          sku
          small_image {
            url
          }
          price_range {
            maximum_price {
              final_price {
                currency
                value
              }
            }
          }
        }
        quantity
        prices {
          price {
            currency
            value
          }
          total_item_discount {
            currency
            value
          }
        }
      }
      prices {
        grand_total {
          currency
          value
        }
        subtotal_excluding_tax {
          currency
          value
        }
        subtotal_including_tax {
          currency
          value
        }
        applied_taxes {
          amount {
            currency
            value
          }
          label
        }
        discounts {
          amount {
            currency
            value
          }
          label
        }
      }
      shipping_addresses {
        selected_shipping_method {
          amount {
            currency
            value
          }
          carrier_code
          carrier_title
          method_code
          method_title
        }
      }
    }
  }
`;

const CREATE_EMPTY_CART = gql`
  mutation createEmptyCart {
    createEmptyCart
  }
`;

const ADD_SIMPLE_PRODUCTS_TO_CART = gql`
  mutation addSimpleProductsToCart($cartId: String!, $cartItems: [SimpleProductCartItemInput!]!) {
    addSimpleProductsToCart(input: {
      cart_id: $cartId
      cart_items: $cartItems
    }) {
      cart {
        id
        items {
          id
          product {
            id
            name
            sku
            small_image {
              url
            }
            price_range {
              maximum_price {
                final_price {
                  currency
                  value
                }
              }
            }
          }
          quantity
          prices {
            price {
              currency
              value
            }
            total_item_discount {
              currency
              value
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
          subtotal_excluding_tax {
            currency
            value
          }
          subtotal_including_tax {
            currency
            value
          }
          applied_taxes {
            amount {
              currency
              value
            }
            label
          }
          discounts {
            amount {
              currency
              value
            }
            label
          }
        }
      }
    }
  }
`;

const ADD_BUNDLE_PRODUCTS_TO_CART = gql`
  mutation addBundleProductsToCart($cartId: String!, $cartItems: [BundleProductCartItemInput!]!) {
    addBundleProductsToCart(input: {
      cart_id: $cartId
      cart_items: $cartItems
    }) {
      cart {
        id
        items {
          id
          product {
            id
            name
            sku
            small_image {
              url
            }
            price_range {
              maximum_price {
                final_price {
                  currency
                  value
                }
              }
            }
          }
          quantity
          prices {
            price {
              currency
              value
            }
            total_item_discount {
              currency
              value
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
          subtotal_excluding_tax {
            currency
            value
          }
          subtotal_including_tax {
            currency
            value
          }
          applied_taxes {
            amount {
              currency
              value
            }
            label
          }
          discounts {
            amount {
              currency
              value
            }
            label
          }
        }
      }
    }
  }
`;

const UPDATE_CART_ITEMS = gql`
  mutation updateCartItems($cartId: String!, $cartItems: [CartItemUpdateInput!]!) {
    updateCartItems(input: {
      cart_id: $cartId
      cart_items: $cartItems
    }) {
      cart {
        id
        items {
          id
          product {
            id
            name
            sku
            small_image {
              url
            }
            price_range {
              maximum_price {
                final_price {
                  currency
                  value
                }
              }
            }
          }
          quantity
          prices {
            price {
              currency
              value
            }
            total_item_discount {
              currency
              value
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
          subtotal_excluding_tax {
            currency
            value
          }
          subtotal_including_tax {
            currency
            value
          }
          applied_taxes {
            amount {
              currency
              value
            }
            label
          }
          discounts {
            amount {
              currency
              value
            }
            label
          }
        }
      }
    }
  }
`;

const REMOVE_ITEM_FROM_CART = gql`
  mutation removeItemFromCart($cartId: String!, $cartItemId: Int!) {
    removeItemFromCart(input: {
      cart_id: $cartId
      cart_item_id: $cartItemId
    }) {
      cart {
        id
        items {
          id
          product {
            id
            name
            sku
            small_image {
              url
            }
            price_range {
              maximum_price {
                final_price {
                  currency
                  value
                }
              }
            }
          }
          quantity
          prices {
            price {
              currency
              value
            }
            total_item_discount {
              currency
              value
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
          subtotal_excluding_tax {
            currency
            value
          }
          subtotal_including_tax {
            currency
            value
          }
          applied_taxes {
            amount {
              currency
              value
            }
            label
          }
          discounts {
            amount {
              currency
              value
            }
            label
          }
        }
      }
    }
  }
`;

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CART_ID':
      return { ...state, cartId: action.payload };
    case 'SET_CART_DATA':
      return { ...state, cartData: action.payload };
    case 'SET_IS_MINI_CART_OPEN':
      return { ...state, isMiniCartOpen: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
};

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    cartId: null,
    cartData: null,
    isMiniCartOpen: false,
    loading: false,
    error: null
  });

  const [createEmptyCart] = useMutation(CREATE_EMPTY_CART);
  const [addSimpleProductsToCart] = useMutation(ADD_SIMPLE_PRODUCTS_TO_CART);
  const [addBundleProductsToCart] = useMutation(ADD_BUNDLE_PRODUCTS_TO_CART);
  const [updateCartItems] = useMutation(UPDATE_CART_ITEMS);
  const [removeItemFromCart] = useMutation(REMOVE_ITEM_FROM_CART);

  // Query cart details when cartId exists
  const { data: cartData, refetch: refetchCart } = useQuery(GET_CART_DETAILS, {
    variables: { cartId: state.cartId },
    skip: !state.cartId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      dispatch({ type: 'SET_CART_DATA', payload: data.cart });
    },
    onError: (error) => {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  });

  // Initialize cart on mount
  const initializeCart = async (force = false) => {
    let cartId = localStorage.getItem('cartId');

    if (!cartId || force) {
      try {
        const result = await createEmptyCart();
        cartId = result.data.createEmptyCart;
        localStorage.setItem('cartId', cartId);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create cart' });
        return;
      }
    }

    dispatch({ type: 'SET_CART_ID', payload: cartId });
  };

  useEffect(() => {
    initializeCart();
  }, [createEmptyCart]);

  // Listen to auth events to reinitialize cart
  useAuthEvents({
    [AUTH_EVENTS.LOGOUT]: () => {
      initializeCart(true);
    },
    [AUTH_EVENTS.TOKEN_VALIDATED]: (userData) => {
      initializeCart(true);
    },
    [AUTH_EVENTS.TOKEN_INVALID]: () => {
      initializeCart(true);
    },
  });

  const addToCart = async (sku, quantity = 1) => {
    if (!state.cartId) return;

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const result = await addSimpleProductsToCart({
        variables: {
          cartId: state.cartId,
          cartItems: [{
            data: {
              quantity,
              sku
            }
          }]
        }
      });

      // Update local state with response data
      if (result.data?.addSimpleProductsToCart?.cart) {
        dispatch({ type: 'SET_CART_DATA', payload: result.data.addSimpleProductsToCart.cart });
      }

      // Also refetch to ensure consistency
      await refetchCart();
      dispatch({ type: 'SET_IS_MINI_CART_OPEN', payload: true });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const addBundleToCart = async (sku, quantity = 1, bundleOptions = []) => {
    if (!state.cartId) return;

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const result = await addBundleProductsToCart({
        variables: {
          cartId: state.cartId,
          cartItems: [{
            data: {
              quantity,
              sku
            },
            bundle_options: bundleOptions
          }]
        }
      });

      // Update local state with response data
      if (result.data?.addBundleProductsToCart?.cart) {
        dispatch({ type: 'SET_CART_DATA', payload: result.data.addBundleProductsToCart.cart });
      }

      // Also refetch to ensure consistency
      await refetchCart();
      dispatch({ type: 'SET_IS_MINI_CART_OPEN', payload: true });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateQuantity = async (cartItemId, quantity) => {
    if (!state.cartId) return;

    try {
      const result = await updateCartItems({
        variables: {
          cartId: state.cartId,
          cartItems: [{
            cart_item_id: cartItemId,
            quantity
          }]
        }
      });

      // Update local state with response data
      if (result.data?.updateCartItems?.cart) {
        dispatch({ type: 'SET_CART_DATA', payload: result.data.updateCartItems.cart });
      }

      await refetchCart();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  const removeItem = async (cartItemId) => {
    if (!state.cartId) return;

    try {
      const result = await removeItemFromCart({
        variables: {
          cartId: state.cartId,
          cartItemId: parseInt(cartItemId)
        }
      });

      // Update local state with response data
      if (result.data?.removeItemFromCart?.cart) {
        dispatch({ type: 'SET_CART_DATA', payload: result.data.removeItemFromCart.cart });
      }

      await refetchCart();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  const openMiniCart = () => {
    dispatch({ type: 'SET_IS_MINI_CART_OPEN', payload: true });
  };

  const closeMiniCart = () => {
    dispatch({ type: 'SET_IS_MINI_CART_OPEN', payload: false });
  };

  const getCartItemsCount = () => {
    if (!state.cartData?.items) return 0;
    return state.cartData.items.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    ...state,
    addToCart,
    addBundleToCart,
    updateQuantity,
    removeItem,
    openMiniCart,
    closeMiniCart,
    getCartItemsCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};